/**
 * Parseo y formateo de números en formato argentino (punto = miles, coma = decimal).
 *
 * Antes esto estaba duplicado en 4 formularios, con dos problemas:
 *  - `normalizePrice` borraba TODOS los puntos, así que si le llegaba un número
 *    de JavaScript (2.70) lo convertía en 270 — multiplicaba por 10 o por 100.
 *  - 3 de las 4 copias hacían `value.replace(...)` sin castear, así que con un
 *    número directamente explotaban con "replace is not a function".
 *
 * Estas versiones aceptan tanto los strings con formato ("1.234,56") como
 * números reales (1234.56), para que la base de datos pueda pasar a guardar
 * números sin romper ninguna pantalla.
 */

/**
 * Convierte a número lo que venga: número, string con formato argentino,
 * string plano, vacío o null. Nunca devuelve NaN.
 */
export const aNumero = (valor) => {
  if (typeof valor === 'number') return Number.isFinite(valor) ? valor : 0;
  if (valor === null || valor === undefined || valor === '') return 0;

  let texto = String(valor).trim().replace(/[^\d.,-]/g, '');
  if (!texto) return 0;

  if (texto.includes(',')) {
    // Hay coma: la coma es el decimal y los puntos son separadores de miles.
    texto = texto.replace(/\./g, '').replace(',', '.');
  } else {
    // Sin coma, el punto es ambiguo. Un separador de miles siempre agrupa de a
    // 3 dígitos: "1.234" son mil doscientos treinta y cuatro, pero "2.70" no
    // puede ser miles (dos dígitos), así que es decimal.
    const partes = texto.split('.');
    const esSeparadorDeMiles =
      partes.length > 1 && partes.slice(1).every((p) => p.length === 3);
    if (esSeparadorDeMiles) texto = partes.join('');
  }

  const n = Number(texto);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Formatea para mostrar en un input mientras se escribe: agrupa los miles con
 * puntos y deja la coma decimal. Tolera que el valor sea un número.
 *
 * Conserva la coma final mientras se tipea ("1234," -> "1.234,") para no
 * pelearse con el usuario a mitad de camino.
 */
export const formatearConPuntos = (valor) => {
  if (valor === null || valor === undefined || valor === '') return '';

  // Si es número, se pasa a formato argentino antes de agrupar.
  const texto =
    typeof valor === 'number'
      ? String(valor).replace('.', ',')
      : String(valor);

  const limpio = texto.replace(/[^\d,]/g, '');
  if (!limpio) return '';

  let [entero, decimal] = limpio.split(',');
  entero = entero.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return decimal !== undefined ? `${entero},${decimal}` : entero;
};

/**
 * Limpia lo que tipeó el usuario para guardarlo en el estado: saca los puntos
 * de miles (que un input controlado puede terminar mezclando adentro del
 * valor mientras se tipea) y deja solo dígitos + una coma decimal.
 *
 * Se usa en el onChange, ANTES de guardar en el estado — a diferencia de
 * `formatearConPuntos`, que es solo para mostrar. Guardar el valor sin
 * limpiar hace que un punto de miles quede pegado en medio del número
 * ("5.5000" en vez de "55000"), y `aNumero` lo interpreta mal.
 */
export const limpiarInput = (valor) => {
  if (valor === null || valor === undefined) return '';

  const texto =
    typeof valor === 'number'
      ? String(valor).replace('.', ',')
      : String(valor);

  const limpio = texto.replace(/[^\d,]/g, '');
  if (!limpio) return '';

  const [entero, decimal] = limpio.split(',');
  return decimal !== undefined ? `${entero},${decimal}` : entero;
};
